<script lang="ts">
    import { onMount } from "svelte";
    import Container from "../../../components/Container.svelte";
    import Gap from "../../../components/Gap.svelte";
    import LightTheme from "../../../components/LightTheme.svelte";
    import PageTransition from "../../../components/PageTransition.svelte";
    import Image from "../../../components/Image.svelte";
    import Hero from "../../../components/Hero.svelte";
    import IdeaInfo from "../../../components/IdeaInfo.svelte";
    import Divider from "../../../components/Divider.svelte";
    import Headline from "../../../components/Headline.svelte";
    import Btn from "../../../components/Btn.svelte";
    import { params } from "@roxi/routify";
    import Subline from "../../../components/Subline.svelte";

    export let url: string;

    enum ProjectStyle {
        TOP_IMG_BOTTOM_CONTENT = 1,
        LEFT_IMG_RIGHT_CONTENT = 2,
    }

    type Project = {
        id: number;
        title: string;
        startdate: string;
        enddate: string;
        bodyhtml: string;
        image: string;
        url: string;
        projectcategoryid: number;
        hidden: boolean;
        date: string;
        stylevariant: ProjectStyle;
        projectcontenthtml: string;
        ordervolume: number;
        involvedpartners: string;
        involvedmembers: string;
        projectclient: string;
    };

    const loadData = async (): Promise<Project> => {
        const data: Project = await fetch(`/api/projects/${$params.id}/${$params.url}`)
            .then(data => data.json())
            .then(data => {
                if (data.startdate && data.enddate)
                    data.date =
                        new Date(data.startdate).toLocaleDateString() +
                        " - " +
                        new Date(data.enddate).toLocaleDateString();
                else if (data.startdate) data.date = new Date(data.startdate).toLocaleDateString();
                else if (data.enddate) data.date = new Date(data.enddate).toLocaleDateString();
                else data.date = "";
                return data;
            })
            .catch(err => console.error(err));

        return data ? Promise.resolve<Project>(data) : Promise.reject(null);
    };
</script>

<LightTheme darken_menu />

<Gap fill_header />

{#await loadData()}
    News laden...
{:then project}
    {#if project.stylevariant == ProjectStyle.LEFT_IMG_RIGHT_CONTENT}
        <Container>
            <div class="LEFT_IMG_RIGHT_CONTENT">
                <Image
                    src={project.image ||
                        "https://liveicom.azureedge.net/sinus/img/defaults/sinus_placeholder_dark.svg"}
                    rounded
                />
                <div class="short-text">
                    <Headline small>{project.title}</Headline>
                    <!-- <Divider /> -->
                    <Subline>{project.date}</Subline>
                </div>
            </div>
        </Container>
    {:else}
        <Container small>
            <Image
                src={project.image ||
                    "https://liveicom.azureedge.net/sinus/img/defaults/sinus_placeholder_dark.svg"}
                rounded
            >
                <time>{project.date}</time>
            </Image>
            <Headline small>{project.title}</Headline>
            <Divider gap="2rem" />
        </Container>
    {/if}

    <Container small>
        <article>
            {#if project.projectclient}
                <Subline mb=".3em">Auftraggeber</Subline>
                {project.projectclient}
            {/if}

            {#if project.involvedmembers}
                <Gap height="3rem" />
                <Subline mb=".3em">Beteiligte Projektmitarbeiter</Subline>
                {project.involvedmembers}
            {/if}

            {#if project.involvedpartners}
                <Gap height="3rem" />
                <Subline mb=".3em">Beteiligte Partner</Subline>
                {project.involvedpartners}
            {/if}

            {#if project.projectcontenthtml.length > 16}
                <Gap height="3rem" />
                <Subline mb=".3em">Projektinhalte</Subline>
                {@html project.projectcontenthtml}
            {/if}

            {#if project.bodyhtml.length > 16}
                <Gap height="3rem" />
                <Subline mb=".3em">Kurzbeschreibung</Subline>
                {@html project.bodyhtml}
            {/if}

            {#if project.ordervolume}
                <Gap height="3rem" />
                <Subline mb=".3em">Auftragsvolumen</Subline>
                {project.ordervolume.toLocaleString("de-DE", {
                    style: "currency",
                    currency: "EUR",
                })}
            {/if}
        </article>

        <Gap height="4rem" />
        <Btn arrow href="aktuelles/news">Zu den News</Btn>
    </Container>
{:catch}
    News konnten nicht geladen werden.
{/await}

<style lang="scss">
    @import "../../../vars";

    h1 {
        font-size: 2.5rem;
        font-weight: 600;
        line-height: 1.2;
    }

    .LEFT_IMG_RIGHT_CONTENT {
        display: grid;
        grid-template-columns: 1fr 2.5fr;
        align-items: center;
        gap: 2rem;
        border: solid 1px $gray;
        border-radius: 1rem;
        @media (max-width: 800px) {
            grid-template-columns: 1fr;
        }
    }

    time {
        position: absolute;
        bottom: 0;
        left: 0;
        background-color: $grayLight;
        color: $grayDark;
        padding: 1rem;
        padding-left: 0;
        padding-bottom: 0;
        border-top-right-radius: 1rem;
    }
</style>
